import { isEqual } from 'lodash-es';

import type {
  SearchKeywordPredicate,
  SearchResponseFilterOutput,
} from '@iconik-io/iconik-api/src/services/search';
import type {
  FacetValues,
  FilterValue,
  FilterValues,
  MultiSelectFilterGroup,
  MultiSelectFilterModifier,
} from '@iconik-io/shared-lib/src/redux/common_filters/types';

import { SEARCH_QUERY_FILTER_KEY } from '../search/active_filter_chips';
import type { NaturalSearchAdjustments } from './types';
import type { NaturalSearchPanelValues } from './utils';

/**
 * How a natural-search answer's filters were put in the panel: the filters
 * themselves, and the panel values each of them produced. It is what tells a
 * filter the user set apart from one the answer did, and what lets a replay
 * send back, untouched, every filter the user left as it was.
 */
export type NaturalSearchProjection = NaturalSearchPanelValues & {
  applied: SearchResponseFilterOutput[];
  /** The words the answer applied, as the panel's query shows them. */
  query: string;
};

/** The panel as a replay reads it. */
export type NaturalSearchPanelState = {
  query: string;
  filterValues: FilterValues;
  facetValues: FacetValues;
  disabledFilters?: string[];
};

const MODIFIER_PREDICATES: Record<
  MultiSelectFilterModifier,
  Pick<SearchKeywordPredicate, 'op' | 'negate'>
> = {
  include_all_of: { op: 'all', negate: false },
  include_any_of: { op: 'in', negate: false },
  exclude_all_of: { op: 'all', negate: true },
  exclude_any_of: { op: 'in', negate: true },
};

/** Whether an entry carries the query's words rather than a condition on a field. */
const isTextEntry = ({ predicate }: SearchResponseFilterOutput): boolean =>
  predicate.kind === 'lexical' ||
  predicate.kind === 'semantic' ||
  'terms' in predicate ||
  'text' in predicate;

const keywordEntry = (
  field: string,
  predicate: SearchKeywordPredicate,
): SearchResponseFilterOutput => ({
  field,
  predicate: {
    kind: 'keyword',
    op: predicate.op,
    value: predicate.value,
    ...(predicate.negate ? { negate: true } : {}),
  },
  // A filter the user set scopes the results, it does not only rank them.
  role: 'filter',
});

const isMultiSelectGroup = (value: unknown): value is MultiSelectFilterGroup =>
  typeof value === 'object' &&
  value !== null &&
  'modifier' in value &&
  'items' in value &&
  Array.isArray(value.items);

const isRange = (value: unknown): value is { min?: string; max?: string } =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  ('min' in value || 'max' in value);

/**
 * A multi-select group on several fields at once (the storage filter, whose
 * items each name the field they are stored under) becomes one entry per field.
 */
const multiSelectGroupEntries = (
  field: string,
  group: MultiSelectFilterGroup,
): SearchResponseFilterOutput[] => {
  const itemsByField = new Map<string, string[]>();
  for (const item of group.items) {
    const itemField = group.itemTermNames?.[item] ?? field;
    itemsByField.set(itemField, [...(itemsByField.get(itemField) ?? []), item]);
  }
  const { op, negate } = MODIFIER_PREDICATES[group.modifier];
  return Array.from(itemsByField, ([itemField, items]) =>
    keywordEntry(itemField, { op, negate, value: items }),
  );
};

/**
 * The filters a panel value stands for, for a value the user set: every value
 * shape the panel holds, on the field it is keyed by. A field the search cannot
 * filter on is refused by the replay, which tells the user so.
 */
const filterValueEntries = (
  key: string,
  value: FilterValue,
): SearchResponseFilterOutput[] => {
  if (typeof value === 'boolean') {
    return [keywordEntry(key, { op: 'eq', value })];
  }
  if (typeof value === 'string') {
    // The panel's boolean select is backed by the strings 'true'/'false'.
    if (value === 'true' || value === 'false') {
      return [keywordEntry(key, { op: 'eq', value: value === 'true' })];
    }
    return value ? [keywordEntry(key, { op: 'eq', value })] : [];
  }
  if (Array.isArray(value)) {
    if (value.every(isMultiSelectGroup)) {
      return value.flatMap((group) => multiSelectGroupEntries(key, group));
    }
    const items = value.filter((item) => typeof item === 'string');
    return items.length > 0
      ? [keywordEntry(key, { op: 'in', value: items })]
      : [];
  }
  if (isRange(value)) {
    const bound = {
      ...(value.min ? { gte: value.min } : {}),
      ...(value.max ? { lte: value.max } : {}),
    };
    return Object.keys(bound).length > 0
      ? [keywordEntry(key, { op: 'range', value: bound })]
      : [];
  }
  return [];
};

const keywordValues = ({ predicate }: SearchResponseFilterOutput): string[] =>
  'op' in predicate
    ? (Array.isArray(predicate.value) ? predicate.value : [predicate.value])
        .flat()
        .filter((value) => typeof value !== 'object' || value === null)
        .map(String)
    : [];

/**
 * The names the answer gave the values of a changed panel value that it still
 * holds. The search does not look an id up again on a replay: it keeps the name
 * the filter is sent back with, so a value would otherwise lose it.
 */
const withSourceLabels = (
  entries: SearchResponseFilterOutput[],
  sources: SearchResponseFilterOutput[],
): SearchResponseFilterOutput[] => {
  const labels: Record<string, string> = Object.assign(
    {},
    ...sources.map((source) => source.value_labels ?? {}),
  );
  return entries.map((entry) => {
    const held = Object.fromEntries(
      keywordValues(entry).flatMap((value) =>
        labels[value] ? [[value, labels[value]]] : [],
      ),
    );
    return Object.keys(held).length > 0
      ? { ...entry, value_labels: held }
      : entry;
  });
};

const facetValueEntries = (
  key: string,
  items: string[],
): SearchResponseFilterOutput[] =>
  items.length > 0 ? [keywordEntry(key, { op: 'in', value: items })] : [];

/**
 * The filters to replay a natural-search answer with, as the panel now
 * describes it:
 *
 * - a panel value the user left as it was projected sends back the filters it
 *   came from, untouched, with their role and weight;
 * - one the user changed or added is sent as the filter it now shows;
 * - one the user removed or paused (eye toggle) is left out;
 * - filters the panel has no control for are always sent back, except the
 *   query's words when the query is cleared or paused;
 * - the adjustments the panel cannot hold are applied last: filters the user
 *   paused are left out, and those they applied anyway are added.
 */
export function panelToNaturalFilters(
  projection: NaturalSearchProjection,
  panel: NaturalSearchPanelState,
  adjustments: NaturalSearchAdjustments = { added: [], paused: [] },
): SearchResponseFilterOutput[] {
  const disabled = new Set(panel.disabledFilters ?? []);
  const isQueryOff =
    disabled.has(SEARCH_QUERY_FILTER_KEY) || !panel.query.trim();
  const projected = Object.values(projection.sourcesByKey).flat();

  // Compared by value rather than identity, as a projection restored from
  // storage no longer shares its entries with the filters it was made from.
  const entries = projection.applied.filter(
    (entry) =>
      !projected.some((source) => isEqual(source, entry)) &&
      !(isQueryOff && isTextEntry(entry)),
  );

  const collect = <Value>(
    current: Record<string, Value>,
    before: Record<string, Value>,
    toEntries: (key: string, value: Value) => SearchResponseFilterOutput[],
  ) => {
    for (const key of new Set([
      ...Object.keys(before),
      ...Object.keys(current),
    ])) {
      const value = current[key];
      if (value === undefined || disabled.has(key)) {
        continue;
      }
      const sources = projection.sourcesByKey[key] ?? [];
      const untouched = key in before && isEqual(value, before[key]);
      entries.push(
        ...(untouched
          ? sources
          : withSourceLabels(toEntries(key, value), sources)),
      );
    }
  };
  collect(panel.filterValues, projection.filterValues, filterValueEntries);
  collect(panel.facetValues, projection.facetValues, facetValueEntries);

  const has = (
    list: SearchResponseFilterOutput[],
    entry: SearchResponseFilterOutput,
  ) => list.some((other) => isEqual(other, entry));
  return [
    ...entries.filter((entry) => !has(adjustments.paused, entry)),
    ...adjustments.added.filter((entry) => !has(entries, entry)),
  ];
}

/**
 * The panel keys the user set or changed, as opposed to those holding what the
 * answer decided: what a later search can carry over as the user's own filters.
 */
export function getUserSetFilterKeys(
  projection: NaturalSearchProjection,
  panel: NaturalSearchPanelState,
): string[] {
  const changed = <Value>(
    current: Record<string, Value>,
    before: Record<string, Value>,
  ) =>
    Object.keys(current).filter(
      (key) => !(key in before) || !isEqual(current[key], before[key]),
    );
  return [
    ...changed(panel.filterValues, projection.filterValues),
    ...changed(panel.facetValues, projection.facetValues),
  ];
}
